/**
 * main.ts — Phase 5 entry point.
 *
 * Adds a global effects chain (Distortion → Delay → Bitcrusher) that sits
 * between the voice bus and the master output. Bitcrusher's AudioWorklet is
 * loaded on first unlock; if loading fails (older browser, disallowed context)
 * the module falls back to pass-through so the chain still produces sound.
 */

import { AudioEngine } from '@ac-slayer/synth-core';
import { OscillatorModule } from '@ac-slayer/synth-core';
import { FilterModule } from '@ac-slayer/synth-core';
import { VCAModule } from '@ac-slayer/synth-core';
import { EnvelopeModule } from '@ac-slayer/synth-core';
import { LFOModule } from '@ac-slayer/synth-core';
import { SampleAndHoldModule } from '@ac-slayer/synth-core';
import { NoiseModule } from '@ac-slayer/synth-core';
import { EffectsChain } from '@ac-slayer/synth-core';
import {
  defaultEffectRegistry,
  type EffectRegistry,
} from '@ac-slayer/synth-core';
import type { GlobalTargetProvider } from '@ac-slayer/synth-core';
import { EffectsGroup } from './ui/panels/EffectsGroup';
import { VoiceManager } from '@ac-slayer/synth-core';
import type { VoiceTemplate } from '@ac-slayer/synth-core';
import { Transport } from '@ac-slayer/synth-core';
import { TapTempo } from '@ac-slayer/synth-core';
import { Metronome } from '@ac-slayer/synth-core';
import { ArpEngine } from '@ac-slayer/synth-core';
import { Sequencer } from '@ac-slayer/synth-core';
import { PatternStore } from '@ac-slayer/synth-core';
import { SequencerPanel } from './ui/panels/SequencerPanel';
import { SongSequencer } from './audio/SongSequencer';
import { SongPanel } from './ui/panels/SongPanel';
import { AtomSource } from '@ac-slayer/synth-core';
import { AtomPanel } from './ui/panels/AtomPanel';
import { captureWavetable } from '@ac-slayer/synth-core';
import { Recorder } from '@ac-slayer/synth-core';
import { ModRouter } from '@ac-slayer/synth-core';
import { WaveModSource } from '@ac-slayer/synth-core';
import { enumerateTargets } from '@ac-slayer/synth-core';
import { ModArcController } from '@ac-slayer/synth-core';
import { ModulePanel } from './ui/panels/ModulePanel';
import { KeyboardDock } from './ui/panels/KeyboardDock';
import { ModMatrix } from './ui/panels/ModMatrix';
import { PresetBrowser } from './ui/panels/PresetBrowser';
import { UserMenu } from './ui/components/UserMenu';
import { PresetManager } from '@ac-slayer/synth-presets';
import { HistoryManager } from '@ac-slayer/synth-core';
import { SupabasePresetStore } from './presets/SupabasePresetStore';
import { SupabasePatternStore } from './presets/SupabasePatternStore';
import { FACTORY_PRESETS, FART_PRESETS } from './presets/factory';
import { applyInitPresetParams } from './presets/initPreset';
import { showFartStormModal } from './ui/components/FartStormModal';

// Expose on window so you can trigger the popup from the dev console
// for debugging — `window.__fartStorm()`. Harmless in production.
(window as unknown as { __fartStorm: () => void }).__fartStorm = () =>
  showFartStormModal();
import type { Preset } from '@ac-slayer/synth-presets';
import { AuthManager } from './auth/AuthManager';
import { getSupabaseClient } from './supabase/client';
import { MidiEngine } from '@ac-slayer/synth-midi';
import { MidiBindings } from '@ac-slayer/synth-midi';
import { CC } from '@ac-slayer/synth-midi';
import { MidiPicker } from './ui/components/MidiPicker';
import { MidiMonitor } from './ui/components/MidiMonitor';
import { attachTouchPopover } from './ui/components/touchPopover';
import { Randomizer } from '@ac-slayer/synth-core';
import { VoiceControls } from './ui/panels/VoiceControls';
import { MacroBank } from '@ac-slayer/synth-core';
import { Knob } from './ui/components/Knob';
import { OutputMeter } from './ui/components/OutputMeter';
import { Visualizer } from './ui/components/Visualizer';
import { initPWA } from './pwa/registerSW';
import { PwaToast } from './ui/components/PwaToast';
import './ui/theme.css';

/** Pitch bend depth in cents. Default ±2 semitones matches the MIDI standard. */
const PITCH_BEND_RANGE_CENTS = 200;

// Worklet sources ship as strings from @ac-slayer/synth-core/worklets;
// we wrap each in a Blob URL the first time it's needed so the
// AudioContext can `audioWorklet.addModule()` them without a separate
// static-asset fetch.
import {
  BITCRUSHER_WORKLET_SOURCE,
  GRANULAR_WORKLET_SOURCE,
  blobUrlFor,
} from '@ac-slayer/synth-core/worklets';
let bitcrusherWorkletUrl: string | null = null;
function getBitcrusherWorkletUrl(): string {
  if (!bitcrusherWorkletUrl) {
    bitcrusherWorkletUrl = blobUrlFor(BITCRUSHER_WORKLET_SOURCE);
  }
  return bitcrusherWorkletUrl;
}
let granularWorkletUrl: string | null = null;
function getGranularWorkletUrl(): string {
  if (!granularWorkletUrl) {
    granularWorkletUrl = blobUrlFor(GRANULAR_WORKLET_SOURCE);
  }
  return granularWorkletUrl;
}

// PWA — service worker lifecycle + install-prompt capture. Toast is
// mounted at the document root so it floats over everything; the
// install controller is passed to the UserMenu so the dropdown can
// surface an "Install app" entry when the browser advertises eligibility.
const pwaToast = new PwaToast();
document.body.appendChild(pwaToast.element);
const installController = initPWA({
  onNeedRefresh: (reload) => {
    pwaToast.show({
      message: 'A new version is available.',
      action: {
        label: 'Reload',
        onClick: () => void reload(),
      },
    });
  },
  onOfflineReady: () => {
    pwaToast.show({
      message: 'Ready to work offline.',
    });
  },
});

// Read the persisted master-volume setting before the engine is
// constructed so the initial master gain matches the user's last
// saved level. Defaulted to 0.35 when localStorage is empty or
// unavailable (Safari private mode, etc).
const MASTER_VOLUME_KEY = 'evil-synth/master-volume';
const DEFAULT_MASTER_VOLUME = 0.35;
let initialMasterVolume = DEFAULT_MASTER_VOLUME;
try {
  const stored = localStorage.getItem(MASTER_VOLUME_KEY);
  if (stored !== null) {
    const v = parseFloat(stored);
    if (Number.isFinite(v) && v >= 0 && v <= 1) initialMasterVolume = v;
  }
} catch {
  /* localStorage unavailable — keep default */
}

const engine = new AudioEngine({ masterGain: initialMasterVolume });
const transport = new Transport(120);

// ── Template modules — shared parameters, per-voice AudioNodes are in voices.
// Deterministic ids (not random-minted) so preset serialization references
// stay valid across sessions — mod connections refer to modules by these ids.
const osc = new OscillatorModule('osc');
const filter = new FilterModule('filter');
const vca = new VCAModule('vca');
const env = new EnvelopeModule('env');
const lfo = new LFOModule('lfo', transport);
const sh = new SampleAndHoldModule('sh');
const noise = new NoiseModule('noise');
// Element-driven mod source — periodic-table picker, cycling shell
// sequence as a [-1,+1] LFO-like signal. Takes the transport so the
// source's step clock can sync to musical divisions of the BPM
// (instead of free-run Hz only).
const atomSource = new AtomSource('atom', transport);
// Forward-declared so the keyboard's noteOn/noteOff callbacks (which
// run AFTER user gestures) can route through the panel when its
// trigger mode is on. Construction happens later in the file
// alongside the rest of the keyboard-dock panels.
let atomPanel: import('./ui/panels/AtomPanel').AtomPanel | null = null;

// Effect registry — single source of truth for "what effect types
// exist". The EFFECTS palette renders one button per descriptor; the
// dynamic chain mints fresh instances on add. Pre-seeded with every
// built-in effect; future modules just register here.
const effectRegistry: EffectRegistry = defaultEffectRegistry(transport);

// Effect chain — the post-voice signal path. Order is user-editable via
// the EFFECTS rack group; changing it tears down and rebuilds the audio
// edges voiceBus → effect[0] → … → effect[last] → master. Bootstraps
// empty; the default starter chain is built up after audio unlock so
// each effect's init() lands on a live engine.
const effectsChain = new EffectsChain({ registry: effectRegistry });

/**
 * Default effect chain composition built on first audio unlock when no
 * preset has loaded. Sensible flow: dynamics (COMP) → coloration
 * (DIST, RING) → modulation (CHORUS, FLANGE, PHASER) → time (DELAY,
 * REVERB) → digital character (CRUSH).
 *
 * Deterministic ids match the pre-refactor singletons so any mod
 * connections in old presets — which referenced these by id — still
 * find their target after the v3→v4 migration.
 */
const DEFAULT_EFFECT_CHAIN: ReadonlyArray<{ type: string; id: string }> = [
  { type: 'compressor', id: 'compressor' },
  { type: 'distortion', id: 'dist' },
  { type: 'ringmod', id: 'ringmod' },
  { type: 'chorus', id: 'chorus' },
  { type: 'flanger', id: 'flanger' },
  { type: 'phaser', id: 'phaser' },
  { type: 'delay', id: 'delay' },
  { type: 'bitcrusher', id: 'bitcrusher' },
  { type: 'reverb', id: 'reverb' },
];

// Arpeggiator — routes note events before they reach the voice manager.
// Stable id so its state survives preset save/load alongside the other
// modules. Off by default; users flip it on via the ARP panel.
const arp = new ArpEngine(transport, 'arp');
// Wire the arp's handlers: when enabled, its step callback plays notes
// on the voice pool and its release callback takes them off again. When
// disabled, it routes straight through via the pass-through handlers.
arp.setHandlers({
  onStep: (note, velocity, when) => {
    // Use ensureAudio wait-free: the arp only ticks once a note has been
    // held, which requires a user gesture that already unlocked audio.
    voices?.noteOn(note, velocity, when);
  },
  onRelease: (note) => {
    voices?.noteOff(note);
  },
  passthrough: {
    onNoteOn: (note, velocity, when) => {
      voices?.noteOn(note, velocity, when);
    },
    onNoteOff: (note) => {
      voices?.noteOff(note);
    },
  },
});

// Step sequencer — fires notes on transport-synced steps and exposes
// per-step modulation as a router source called STEP. Stable id so
// preset save/load round-trips its state alongside the other modules.
const sequencer = new Sequencer(transport, 'sequencer');
sequencer.setHandlers({
  onStep: (note, velocity, when) => {
    voices?.noteOn(note, velocity, when);
  },
  onRelease: (note) => {
    voices?.noteOff(note);
  },
});

// Default patch: saw bass, LP sweep, med envelope, slow triangle LFO ready to
// wobble the cutoff.
osc.waveform.set('saw', 'init');
osc.coarse.set(-12, 'init');
osc.level.set(0.8, 'init');
filter.cutoff.set(1800, 'init');
filter.resonance.set(4, 'init');
vca.gain.set(0.85, 'init');
env.attack.set(0.005, 'init');
env.decay.set(0.25, 'init');
env.sustain.set(0.55, 'init');
env.release.set(0.35, 'init');
lfo.waveform.set('triangle', 'init');
lfo.rate.set(2.5, 'init');
sh.rate.set(6, 'init');
sh.slew.set(0, 'init');
// Effect defaults — applied immediately after the chain is built on
// audio unlock, by reading the live module from the chain. The map
// keys correspond to effect types in the registry; values mutate the
// freshly-minted Parameter instances. Listed here so the bootstrap
// sounds intentional (no random-default soup) without coupling main
// to the concrete classes.
const EFFECT_DEFAULTS: Readonly<Record<string, Record<string, number>>> = {
  distortion: { Drive: 0.25, Tone: 5000, Mix: 0.4 },
  delay: { Time: 0.28, Feedback: 0.45, Mix: 0.25 },
  bitcrusher: { Bits: 12, Freq: 16000, Mix: 0.2 },
};

const template: VoiceTemplate = { osc, filter, vca, env };

let voices: VoiceManager | null = null;
let router: ModRouter | null = null;
let matrix: ModMatrix | null = null;
/** WAVE mod source — taps a chosen audio node and exposes its signal
 *  in the Mod Matrix. Built per-engine since it owns AudioNodes. */
let waveModSource: WaveModSource | null = null;
/** Module-scope reference to the preset browser, populated only when
 *  Supabase is configured. Null otherwise. Used by the randomizer to
 *  clear the active-preset label after a roll, and potentially by other
 *  features that need to know which patch is currently loaded. */
let presetBrowser: PresetBrowser | null = null;

/** Shared pitch-bend signal. Created on audio unlock; connected to every
 *  voice's osc.detune. Offset is in cents. */
let pitchBendSource: ConstantSourceNode | null = null;

/** Mod-wheel signal — preset-scope mod source. Offset rides 0..1 from
 *  CC 1 messages and the router fans it out to whatever the user has
 *  routed it to in the matrix. Created on audio unlock; null before. */
let modWheelSource: ConstantSourceNode | null = null;

/** Drive pitch bend from any source (MIDI or keyboard). `normalized` ∈ [-1,+1]. */
function setPitchBend(normalized: number): void {
  if (!pitchBendSource) return;
  const cents = Math.max(-1, Math.min(1, normalized)) * PITCH_BEND_RANGE_CENTS;
  const t = engine.context.currentTime;
  const offset = pitchBendSource.offset;
  offset.cancelScheduledValues(t);
  offset.setTargetAtTime(cents, t, 0.01);
}

/** Drive the mod-wheel signal. `normalized` ∈ [0,1] (CC value / 127). */
function setModWheel(normalized: number): void {
  if (!modWheelSource) return;
  const v = Math.max(0, Math.min(1, normalized));
  const t = engine.context.currentTime;
  const offset = modWheelSource.offset;
  offset.cancelScheduledValues(t);
  offset.setTargetAtTime(v, t, 0.01);
}

// ── Supabase client + auth + preset store. The client construction reads
// env vars (VITE_SUPABASE_URL/_ANON_KEY) — if they're missing we surface
// the error visibly in the DOM rather than blowing up silently.
let supabaseReady = true;
let authManager: AuthManager | null = null;
let presetManager: PresetManager | null = null;
/** Sequencer pattern store — gets a Supabase-backed implementation
 *  when auth is configured (so saved patterns sync across devices),
 *  or a plain in-memory + localStorage one as fallback. Filled in
 *  inside the supabase try below; the seq panel construction site
 *  uses whatever's here. */
let patternStore: PatternStore | undefined;
/** Stack-based undo/redo over full preset captures. Constructed once
 *  presetManager is in place so the capture/apply hooks are ready. */
let history: HistoryManager<import('@ac-slayer/synth-presets').PresetCapture> | null =
  null;

// PresetManager setup is below voiceControls's construction (which happens
// after MIDI engine is created above).

function requireEl(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Expected #${id} in the DOM.`);
  return el;
}

/**
 * Build a labeled sub-section inside a merged module panel (SOURCE, MOD).
 * Returns the body element — pass it as `host` to `addParamControl` so
 * the controls land in this group. Each sub-section is its own flex row;
 * the parent panel content stacks them vertically without forcing the
 * outer panel any wider than the widest row of knobs.
 */
function makeSubsection(
  host: HTMLElement,
  label: string,
  /** Optional modifier slug — appended as `module-panel-subgroup-<slug>`
   *  so the CSS can target specific sub-groups (e.g. NOISE gets a
   *  border-left accent). */
  modifier?: string,
): HTMLElement {
  // Tag the host the first time we add a subgroup so CSS can switch
  // the default row-wrap layout into a vertical stack — each subgroup
  // gets its own row and the panel's width is just the widest one.
  host.classList.add('module-panel-content-grouped');
  const group = document.createElement('div');
  group.className = modifier
    ? `module-panel-subgroup module-panel-subgroup-${modifier}`
    : 'module-panel-subgroup';
  const labelEl = document.createElement('div');
  labelEl.className = 'module-panel-subsection label';
  labelEl.textContent = label;
  group.appendChild(labelEl);
  const body = document.createElement('div');
  body.className = 'module-panel-subgroup-body';
  group.appendChild(body);
  host.appendChild(group);
  return body;
}

const statusEl = requireEl('engine-status');
const stage = requireEl('stage');
const keyboardSlot = requireEl('keyboard-slot');
const matrixSlot = requireEl('matrix-slot');
const presetsSlot = requireEl('presets-slot');
const presetsToggle = requireEl('presets-toggle') as HTMLButtonElement;
const midiSlot = requireEl('midi-slot');
const randomizeBtn = requireEl('randomize-btn') as HTMLButtonElement;
const undoBtn = requireEl('undo-btn') as HTMLButtonElement;
const redoBtn = requireEl('redo-btn') as HTMLButtonElement;
const bpmInput = requireEl('bpm-input') as HTMLInputElement;
const panicBtn = requireEl('panic-btn') as HTMLButtonElement;
const recordBtn = requireEl('record-btn') as HTMLButtonElement;
const meterSlot = requireEl('meter-slot');
const masterVolumeInput = requireEl('master-volume') as HTMLInputElement;

// ── Master volume — drives engine.master.gain. The initial value
// was loaded from localStorage at the top of the file so the
// AudioEngine constructor saw it; this just wires the slider for
// live drags + persistence. Audio writes use setTargetAtTime so a
// fast drag doesn't zipper-noise.
masterVolumeInput.value = String(initialMasterVolume);
masterVolumeInput.addEventListener('input', () => {
  const v = parseFloat(masterVolumeInput.value);
  if (!Number.isFinite(v)) return;
  applyMasterVolume(v);
  try {
    localStorage.setItem(MASTER_VOLUME_KEY, String(v));
  } catch {
    /* save best-effort; UI keeps working */
  }
});

/**
 * Push a new master gain into the engine. No-op pre-unlock — the
 * gain node doesn't exist yet, but the AudioEngine constructor
 * already received the persisted value so first-sound is at the
 * right level regardless.
 */
function applyMasterVolume(v: number): void {
  if (engine.state === 'closed') return;
  try {
    const t = engine.context.currentTime;
    const param = engine.master.gain;
    param.cancelScheduledValues(t);
    param.setTargetAtTime(v, t, 0.01);
  } catch {
    /* engine not yet built */
  }
}

// ── Randomize — dice button in the topbar. Default: full randomize.
// Shift-click for 25% "nudge" (lerp each param a quarter of the way toward
// a new random target) — useful for wiggling a known-good patch.
//
// The non-effect modules are static (always present); effects come from
// the live chain so dynamic add/remove flows through Randomize too.
const STATIC_RANDOM_MODULES = [osc, filter, vca, env, lfo, sh, noise];
const randomizer = new Randomizer();
randomizeBtn.addEventListener('click', (e) => {
  const amount = e.shiftKey ? 0.25 : 1;

  // Checkpoint pre-randomize state so a single Ctrl+Z lands the user
  // back on the patch they had a moment ago. Skip while the manager
  // is itself applying (defensive — this path isn't called from
  // inside applyState).
  if (history && !history.isApplying()) history.push();

  // Hard-cut anything currently sounding. A previous patch may have
  // left a note in its release tail or a hung voice from a
  // misbehaving envelope; without this clear, the new randomized
  // params get smeared on top of stale state and the next patch
  // can sound like garbage. Cheap (silences the voice pool only;
  // effect tails are short enough to dissipate naturally).
  //
  // Output protection during the rebuild is handled by the
  // safety limiter the engine inserts between master and the
  // device — no manual ducking needed here, which previously
  // caused rapid clicks during a duck-ramp to capture mid-ramp
  // values and silently decay the master gain on subsequent rolls.
  voices?.panic();

  // Full randomize regenerates the effects bank itself — random count
  // (capped at 4), random types, duplicates allowed — BEFORE we
  // randomize params, so the freshly-minted effect instances get
  // their knobs randomized in the same pass below. Distortion and
  // bitcrusher are pinned as required so every random patch carries
  // their signature color. Nudge leaves the bank alone.
  if (amount === 1) {
    randomizer.randomizeEffectsBank(
      effectsChain,
      effectRegistry.list().map((d) => d.type),
      {
        amount,
        // Cap at 6 — required types take 4 slots, leaving room for
        // 0–2 extras drawn from the remaining pool. Bigger banks
        // run the audio graph hot and rarely add musical value past
        // this many simultaneous effects.
        maxCount: 6,
        // The four signature character effects must always be present
        // in a random patch — they're what makes the synth sound like
        // *this* synth instead of a generic subtractive box.
        requiredTypes: ['distortion', 'bitcrusher', 'ruin', 'wavefolder'],
        // Modulation effects (chorus / flanger / phaser) all push
        // pitch around in similar ways — stacking two muddies the
        // sound rather than adding character. Force at most one of
        // the trio per random bank.
        exclusiveGroups: [['chorus', 'flanger', 'phaser']],
      },
    );
  }

  // Static modules + every live effect instance — randomize hits both.
  // Re-read the chain after the bank rebuild so the new effects are in
  // this list and pick up randomized params.
  const allModules = [...STATIC_RANDOM_MODULES, ...effectsChain.getModules()];

  // Noise-source probability gate. The NoiseModule was getting rolled
  // on every full randomize, and its built-in `randomSkew: 0.3`
  // (biased low) wasn't enough to keep noise out of the bank — too
  // many random patches ended up with the same hissy character.
  // Now: full-randomize has a 10% chance to include the noise
  // module in the param-randomization pass; the other 90% the
  // noise module is skipped AND its level is forced to 0 so any
  // leftover value from the previous patch doesn't bleed through.
  // Nudges (amount < 1) always include noise so the user's existing
  // noise setting doesn't get muted on a Shift-click "tweak".
  const NOISE_RANDOMIZE_PROBABILITY = 0.1;
  const includeNoise = amount < 1 || Math.random() < NOISE_RANDOMIZE_PROBABILITY;
  const modulesForRandomize = includeNoise
    ? allModules
    : allModules.filter((m) => m !== noise);

  randomizer.randomizeModules(modulesForRandomize, { amount });

  // Force noise to silence on the 90% of full rolls that excluded it
  // — the previous patch may have left it loud and we want a clean
  // tonal baseline.
  if (amount === 1 && !includeNoise) {
    noise.level.set(0, 'randomize');
  }
  // Glide isn't inside a SynthModule — it's a voice-level control. Randomize
  // it alongside the modules so dice rolls can introduce portamento too.
  randomizer.randomizeParams(
    [
      voiceControls.glide,
      voiceControls.unison,
      voiceControls.spread,
      ...macroBank.macros.map((m) => m.value),
    ],
    { amount },
  );
  // Re-wire the mod matrix. Full randomize drops the old connections and
  // rolls 2–4 fresh ones with varied sources + destinations; nudge just
  // wiggles existing depths. Router is null pre-unlock — skip then.
  // Targets reflect the NEW chain composition since the bank rebuild
  // above already fired chain listeners that refreshed the router's
  // target list.
  if (router) {
    randomizer.randomizeModConnections(router, currentTargets(), { amount });
  }

  // Audibility floor — random param combos sometimes land in zones
  // that produce inaudible output (VCA gain near 0, filter cutoff
  // way down, sustain at 0 with no decay tail, compressor crushing
  // everything to silence). This pass only RAISES params that fell
  // below their safe minimums; it never lowers anything, so the
  // random character of the patch elsewhere is preserved.
  ensureAudiblePatch();

  // Mod-aware audibility — separate pass because static floors above
  // can be cancelled out by negative-depth mod connections. e.g. a
  // baseline cutoff of 600 Hz looks fine at rest but a -0.4 LFO mod
  // (modScale 4000) pulls it to -1000 Hz at the trough — silent for
  // half the LFO cycle. This pass bumps the static baseline by the
  // worst-case negative mod contribution so the modulated floor
  // stays audible.
  ensureModulatedAudibility();

  // The patch is no longer the loaded preset — drop the active-preset
  // label so the toolbar doesn't keep claiming "Brass Stabs" while the
  // user is hearing dice-roll soup. Skip on a Shift+Click "nudge" since
  // that's an intentional small wiggle of the existing patch, not a
  // rebuild from scratch.
  if (amount === 1) presetBrowser?.clearActive();
});

/**
 * Bump the handful of params that most commonly result in a silent
 * patch back up to a minimum-audibility floor. Floors were tuned by
 * ear — they're "minimum to actually hear something" not "loud" — so
 * a quiet random patch stays quiet, but a silent one becomes audible.
 */
function ensureAudiblePatch(): void {
  /** [parameter, minimum-acceptable-value] tuples — these only get
   *  bumped UP when they're below the floor. Order doesn't matter;
   *  each entry is independent. Add new entries here when a future
   *  random combo reveals another dead zone. */
  const floors: ReadonlyArray<readonly [{ value: number; set(v: number, src: 'randomize'): void }, number]> = [
    [vca.gain, 0.4],
    [osc.level, 0.5],
    [filter.cutoff, 400],
    [env.sustain, 0.25],
    [env.release, 0.05],
  ];
  for (const [param, min] of floors) {
    if (param.value < min) param.set(min, 'randomize');
  }

  // Envelope attack ceiling. The Envelope randomSkew biases short,
  // but the upper tail still occasionally lands at >1s — short note
  // plays then never reach the sustain plateau before release fires
  // and the patch sounds silent on every keypress. Cap at 800ms so
  // even staccato keypresses produce something audible.
  if (env.attack.value > 0.8) env.attack.set(0.8, 'randomize');

  // 'capture' waveform routes the OSC through an AudioBufferSourceNode
  // reading the user-captured wavetable. If they haven't captured one
  // yet (the common case after Randomize), the buffer is null and the
  // OSC is silent. Fall back to saw so the patch is audible — the user
  // can still pick CAP manually once they've captured a shape.
  if (osc.waveform.value === 'capture' && osc.getCapturedWavetable() === null) {
    osc.waveform.set('saw', 'randomize');
  }

  // Noise ceiling. Pure noise above ~50% drowns the oscillator and
  // makes random patches sound like a broken radio rather than a
  // synth. Capping at 0.5 leaves plenty of headroom for noisy
  // textures while keeping the tonal voice as the dominant source.
  if (noise.level.value > 0.5) noise.level.set(0.5, 'randomize');

  // High-pass cutoff cap. A high-pass set above ~midway on the knob
  // strips out almost everything in the synth's musical range —
  // perceptually identical to a silent patch even though the filter
  // is technically passing the high overtones. Cap to the 50% knob
  // position (geometric mean of 20-20000 Hz ≈ 632 Hz on the exp
  // curve) when the user lands on HP so the bass + low-mids
  // survive. LP / BP / NOTCH are unaffected.
  if (filter.filterType.value === 'highpass' && filter.cutoff.toNormalized() > 0.5) {
    filter.cutoff.setNormalized(0.5, 'randomize');
  }

  // Bandpass with low cutoff + high Q strips the OSC fundamental and
  // most overtones — sounds silent for typical note ranges. Floor
  // bandpass cutoff at 300 Hz (clear of typical bass notes) and cap
  // resonance to 6 so a narrow window doesn't choke the carrier.
  if (filter.filterType.value === 'bandpass') {
    if (filter.cutoff.value < 300) filter.cutoff.set(300, 'randomize');
    if (filter.resonance.value > 6) filter.resonance.set(6, 'randomize');
  }
  // Notch with extreme Q narrowed onto the OSC fundamental can null
  // it out entirely. Cap Q for the notch case so the rest of the
  // spectrum survives.
  if (filter.filterType.value === 'notch' && filter.resonance.value > 4) {
    filter.resonance.set(4, 'randomize');
  }
  // Even on lowpass, a Q above ~20 with a low cutoff turns the filter
  // into a near-self-oscillator that masks the OSC signal — not strictly
  // silent but useless as a tone. Cap globally.
  if (filter.resonance.value > 20) filter.resonance.set(20, 'randomize');

  // Effect-path safeguards.
  for (const m of effectsChain.getModules()) {
    if (m.type === 'compressor') {
      // Compressor with Threshold very low + Ratio high + Makeup
      // near zero crushes the entire signal to silence. Bump Makeup
      // back to a reasonable floor when we're in that regime. Also
      // cap makeup at the high end so a hot patch + 3× boost can't
      // hit the safety limiter so hard the audio sounds pumped.
      const threshold = findNumericParam(m, 'Threshold');
      const ratio = findNumericParam(m, 'Ratio');
      const makeup = findNumericParam(m, 'Makeup');
      if (!threshold || !ratio || !makeup) continue;
      const aggressive = threshold.value < -30 || ratio.value > 6;
      if (aggressive && makeup.value < 0.7) {
        makeup.set(0.7, 'randomize');
      }
      if (makeup.value > 1.6) makeup.set(1.6, 'randomize');
    } else if (m.type === 'distortion') {
      // Distortion with Drive=0 + Mix high is just a transparent
      // pass-through (the waveshaper curve is near-linear at k=1),
      // so the user hears no distortion character even though Mix
      // suggests they should. Floor Drive when Mix is actually
      // audible so a "wet" reading on the meter actually means
      // wet on the ear.
      const drive = findNumericParam(m, 'Drive');
      const mix = findNumericParam(m, 'Mix');
      if (!drive || !mix) continue;
      if (mix.value >= 0.2 && drive.value < 0.25) {
        drive.set(0.25, 'randomize');
      }
    } else if (m.type === 'eq') {
      // 3-band parametric EQ. With all three gains random, the
      // probability of all three landing negative is non-trivial,
      // and if they ALL hit -10 dB or lower the output is buried.
      // Worst case is the mid-peak hitting the OSC fundamental
      // with a high Q — that single band can scoop -18 dB at the
      // exact note frequency. Cap any single band's negative gain
      // and ensure not all three are negative-heavy at once.
      const lo = findNumericParam(m, 'Lo Gain');
      const mid = findNumericParam(m, 'Mid Gain');
      const hi = findNumericParam(m, 'Hi Gain');
      const midQ = findNumericParam(m, 'Mid Q');
      if (!lo || !mid || !hi) continue;
      // Per-band cut floor: never less than -8 dB.
      if (lo.value < -8) lo.set(-8, 'randomize');
      if (mid.value < -8) mid.set(-8, 'randomize');
      if (hi.value < -8) hi.set(-8, 'randomize');
      // Whole-spectrum-scoop guard: if all three remain negative,
      // bring the largest cut back to 0 so SOMETHING is flat.
      if (lo.value < 0 && mid.value < 0 && hi.value < 0) {
        const flattest = Math.max(lo.value, mid.value, hi.value);
        if (lo.value === flattest) lo.set(0, 'randomize');
        else if (hi.value === flattest) hi.set(0, 'randomize');
        else mid.set(0, 'randomize');
      }
      // High-Q narrow notch on the mid band can null the
      // fundamental at almost any frequency. Cap Mid Q so the
      // dip stays musical rather than surgical.
      if (midQ && midQ.value > 4) midQ.set(4, 'randomize');
    } else if (m.type === 'bitcrusher') {
      // Bitcrusher mirror: with Mix audible but Bits near 16 and
      // Freq near nyquist, the wet path is effectively a transparent
      // copy of the dry signal — no crunch, no aliasing. Cap Bits
      // and/or Freq when Mix is up so the effect actually contributes
      // its character. Cap (lower) rather than floor (raise) because
      // for crusher params, smaller values = more audible character.
      const bits = findNumericParam(m, 'Bits');
      const freq = findNumericParam(m, 'Freq');
      const mix = findNumericParam(m, 'Mix');
      if (!bits || !freq || !mix) continue;
      if (mix.value >= 0.2) {
        if (bits.value > 10) bits.set(10, 'randomize');
        if (freq.value > 12000) freq.set(12000, 'randomize');
      }
    }
  }
}

/**
 * Mod-aware companion to `ensureAudiblePatch`. The static floor pass
 * looks at param values at rest, but a randomized mod connection can
 * still pull those values into silence at the trough of an LFO cycle
 * or the peak of an envelope. This pass walks every active connection,
 * sums the worst-case negative contribution per critical target, and
 * bumps the baseline so the modulated minimum stays audible.
 *
 * Critical targets are the few params where "modulated to zero" means
 * "patch is silent": VCA gain, OSC level, FILT cutoff. Anything else
 * (env timing, OSC pitch, effect mix) can swing freely.
 */
function ensureModulatedAudibility(): void {
  if (!router) return;

  type Critical = {
    /** The Parameter to bump if the modulated trough goes too low. */
    readonly param: { value: number; max: number; set(v: number, src: 'randomize'): void };
    /** Audibility floor — modulated value should not drop below this. */
    readonly floor: number;
    /** Mod target's modScale — same as in targets.ts. */
    readonly modScale: number;
  };
  // Use the live module ids so this stays correct if anyone renames.
  const criticals = new Map<string, Critical>([
    [`${vca.id}::Gain`, { param: vca.gain, floor: 0.4, modScale: 1 }],
    [`${osc.id}::Level`, { param: osc.level, floor: 0.4, modScale: 1 }],
    [`${filter.id}::Cutoff`, { param: filter.cutoff, floor: 400, modScale: 4000 }],
  ]);

  // Sum |negative-depth × rangeMul × modScale| per target. Pessimistic:
  // assumes every connection contributes its full negative swing
  // simultaneously, which catches both env-source mods (always 0..1
  // so neg depth = always-negative pull) and LFO/S&H sources (where
  // the trough hits worst-case negative once per cycle).
  const negContrib = new Map<string, number>();
  for (const c of router.listConnections()) {
    const key = `${c.targetModuleId}::${c.targetParam}`;
    const crit = criticals.get(key);
    if (!crit) continue;
    if (c.depth >= 0) continue;
    const range = c.rangeMul ?? 1;
    const contrib = Math.abs(c.depth) * range * crit.modScale;
    negContrib.set(key, (negContrib.get(key) ?? 0) + contrib);
  }

  for (const [key, neg] of negContrib) {
    const crit = criticals.get(key);
    if (!crit) continue;
    const required = Math.min(crit.param.max, crit.floor + neg);
    if (crit.param.value < required) {
      crit.param.set(required, 'randomize');
    }
  }
}

/** Lookup helper — returns the NumericParameter named `name` on the
 *  given module, or null if the module doesn't have it (or it's an
 *  enum). Used by the audibility-floor logic to avoid hardcoding
 *  per-effect class shapes. */
function findNumericParam(
  module: { parameters: readonly { name: string; kind: string; value: number }[] },
  name: string,
): { value: number; set(v: number, src: 'randomize'): void } | null {
  const p = module.parameters.find((x) => x.name === name);
  if (!p || p.kind !== 'numeric') return null;
  return p as unknown as { value: number; set(v: number, src: 'randomize'): void };
}

/**
 * Enumerate mod targets against the live globals + chain. Called from
 * randomize, on-chain-change, and the initial ModRouter boot. Pulls
 * the effects array from the chain so adds/removes flow through
 * automatically.
 */
function currentTargets() {
  return enumerateTargets(
    template,
    {
      lfo,
      sh,
      noise,
      effects: effectsChain.getModules() as readonly GlobalTargetProvider[],
    },
    macroBank,
  );
}

// ── Master meter — tapped off engine.master after first unlock. Construct
// eagerly so the topbar reserves space; `attach()` later wires the audio.
const outputMeter = new OutputMeter();
meterSlot.appendChild(outputMeter.element);

// ── Visualizer (scope / spectrum) — same construct-now, attach-later
// pattern as the meter. Tapped off the same master bus so what the
// user sees lines up with what they hear. Click-to-toggle scope vs
// spectrum; mode persists to localStorage. Mounted into the rack as
// a panel below (after effects), not the topbar — it reads as a
// component of the synth rather than chrome.
const visualizer = new Visualizer();

// ── Panic button — hard-cuts every voice. Voices might be null pre-unlock,
// in which case there's nothing to kill anyway. Brief "armed" visual flash
// gives the user confirmation the click landed.
// ── Global keyboard shortcuts.
// Ctrl / Cmd + R / P / M / A / S → randomize, presets, mod matrix, arp,
// sustain. Skip when the user is typing in a text input so preset-name
// edits don't accidentally trigger them. `preventDefault` blocks the
// browser's own meanings (print, select-all, save, reload) on match.
function isTypingContext(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return ['text', 'email', 'password', 'search', 'url', 'tel', 'number'].includes(
      target.type,
    );
  }
  return false;
}

// ── Easter-egg buffer: Shift + typed letters that, on match, reveal
// the secret fart preset bank. Buffer accumulates lowercase letters
// only when Shift is held (no other modifiers) and outside text inputs;
// any other key, idle timeout, or modifier change resets it.
const FART_UNLOCK_PHRASE = 'fartstorm';
let fartUnlockBuffer = '';
let fartUnlockResetTimer: ReturnType<typeof setTimeout> | null = null;
function resetFartUnlockBuffer(): void {
  fartUnlockBuffer = '';
  if (fartUnlockResetTimer !== null) {
    clearTimeout(fartUnlockResetTimer);
    fartUnlockResetTimer = null;
  }
}
function unlockFartPresets(): void {
  // The celebratory popup ALWAYS fires when the magic word is typed,
  // even if the bank was already unlocked in a prior session. The
  // alternative — gating it on `!fartsUnlocked` — meant typing the
  // phrase a second time silently did nothing, which read as a bug.
  showFartStormModal({
    onDismiss: () => {
      // After the user closes the popup, queue them up with the
      // showpiece patch — load Cheek Flapper, open the preset
      // panel, and scroll its row into view. Wrapped because the
      // browser only exists when Supabase is configured; on a
      // missing browser this whole chain is a quiet no-op.
      const cheekFlapper = FART_PRESETS.find((p) => p.id === 'preset-factory-fart-cheek-flapper');
      if (!cheekFlapper || !presetBrowser) return;
      presetBrowser.loadFactoryPreset(cheekFlapper);
      // Open the panel WITHOUT focusing the search field — the
      // default open behavior auto-focuses search so the user can
      // type to filter, but here we want them to immediately bang
      // on QWERTY to hear Cheek Flapper play. With search focused,
      // letter keys would type into the search box instead of
      // playing notes.
      presetBrowser.setOpen(true, { focusSearch: false });
      // Defer the scroll until after the panel's slide-in
      // animation kicks off — scrollIntoView fires synchronously
      // and the panel is hidden until the open class lands, so
      // running it on the next frame guarantees the row is
      // already laid out and visible.
      requestAnimationFrame(() => {
        presetBrowser?.scrollToPreset(cheekFlapper.id);
      });
      // Belt and suspenders — if anything (the close button, the
      // panel's open animation, etc.) ended up with focus, drop it
      // back to the body so QWERTY note input flows freely.
      if (
        document.activeElement instanceof HTMLElement &&
        document.activeElement !== document.body
      ) {
        document.activeElement.blur();
      }
    },
  });
  if (fartsUnlocked) return;
  fartsUnlocked = true;
  try {
    localStorage.setItem(FART_UNLOCK_KEY, '1');
  } catch {
    // Storage unavailable — unlock still applies for the session.
  }
  presetBrowser?.setFactoryPresets([...FACTORY_PRESETS, ...FART_PRESETS]);
  // Tiny on-screen flash so the user knows the secret took. Plain
  // engine-status repurposes the existing topbar slot — fades back to
  // its normal text after a few seconds.
  const prevStatus = statusEl.textContent;
  statusEl.textContent = '💨 Fart preset bank unlocked';
  setTimeout(() => {
    if (statusEl.textContent === '💨 Fart preset bank unlocked') {
      statusEl.textContent = prevStatus ?? '';
    }
  }, 3000);
}

document.addEventListener('keydown', (e) => {
  // Easter-egg buffer first — runs before the modifier filter so it
  // sees the Shift+letter combo. Skip while typing in inputs so the
  // phrase doesn't accumulate from preset-name edits.
  if (
    e.shiftKey &&
    !e.ctrlKey &&
    !e.metaKey &&
    !e.altKey &&
    !isTypingContext(e.target) &&
    e.key.length === 1 &&
    /[a-zA-Z]/.test(e.key)
  ) {
    fartUnlockBuffer = (fartUnlockBuffer + e.key.toLowerCase()).slice(
      -FART_UNLOCK_PHRASE.length,
    );
    if (fartUnlockResetTimer !== null) clearTimeout(fartUnlockResetTimer);
    // Idle reset — if the user pauses for more than 1.5s mid-phrase,
    // start over. Keeps the buffer from matching across unrelated
    // typing sessions.
    fartUnlockResetTimer = setTimeout(resetFartUnlockBuffer, 1500);
    if (fartUnlockBuffer === FART_UNLOCK_PHRASE) {
      resetFartUnlockBuffer();
      unlockFartPresets();
    }
  } else if (!e.shiftKey) {
    // Releasing or never-holding Shift on a real key resets — the
    // unlock requires the entire phrase typed with Shift held the whole
    // time, matching the user's spec.
    resetFartUnlockBuffer();
  }

  // Undo/redo handled before the modifier-only filter because they
  // need shift-sensitivity (Shift+Cmd+Z = redo on macOS, Ctrl+Y =
  // redo on Windows). Ignore while typing in text inputs.
  const modKey = e.ctrlKey || e.metaKey;
  if (modKey && !e.altKey && !isTypingContext(e.target)) {
    const key = e.key.toLowerCase();
    if (key === 'z') {
      e.preventDefault();
      if (e.shiftKey) history?.redo();
      else history?.undo();
      return;
    }
    if (key === 'y' && !e.shiftKey) {
      e.preventDefault();
      history?.redo();
      return;
    }
  }

  // Accept either Ctrl (Windows/Linux) or Cmd (macOS). Skip when Shift
  // or Alt are involved so we don't steal subtler combos.
  const modifierOnly =
    (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey;
  if (!modifierOnly) return;
  if (isTypingContext(e.target)) return;

  const key = e.key.toLowerCase();
  switch (key) {
    case 'r':
      e.preventDefault();
      randomizeBtn.click();
      break;
    case 'p':
      e.preventDefault();
      presetsToggle.click();
      break;
    case 'm':
      e.preventDefault();
      toggleMatrix();
      break;
    case 'a':
      e.preventDefault();
      keyboard.toggleArp();
      break;
    case 's':
      e.preventDefault();
      keyboard.toggleSustain();
      break;
  }
});

// ── Recorder — taps the master output and writes a 16-bit stereo WAV
// when the user toggles off. The Record button stays disabled until
// audio unlocks (no engine = nothing to capture). One toggle button
// covers both start and stop; visual state distinguishes them.
const recorder = new Recorder();
const recordLabel = recordBtn.querySelector('.topbar-record-label');
// Live duration readout — appended into the record button so it sits
// inside the user's natural focal point during a take. Cleared between
// recordings so an idle button reads as "Record".
const recordTimer = document.createElement('span');
recordTimer.className = 'topbar-record-timer mono';
recordBtn.appendChild(recordTimer);
// Active patch name follows whatever the preset browser reports as the
// loaded patch. Used to build a friendlier WAV filename. Updated below
// once the browser has been wired (subscribeActive happens further down
// in this file); kept null until then.
let activeRecordingPresetName: string | null = null;
function refreshRecordButton(): void {
  const active = recorder.getState() === 'recording';
  recordBtn.classList.toggle('recording', active);
  recordBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
  if (recordLabel) recordLabel.textContent = active ? 'Stop' : 'Record';
  recordBtn.title = active
    ? 'Stop and download the WAV'
    : 'Record master output to WAV — click again to stop and download';
  if (!active) {
    recordTimer.textContent = '';
    recordBtn.style.removeProperty('--record-peak');
  }
}
recorder.subscribe(() => refreshRecordButton());
refreshRecordButton();

// rAF loop that updates the duration readout + peak indicator while a
// recording is active. Self-suspends when idle so we don't burn frames
// outside takes.
let recordRafId: number | null = null;
function recordTick(): void {
  if (recorder.getState() !== 'recording') {
    recordRafId = null;
    return;
  }
  const sec = recorder.getCurrentDurationSec();
  recordTimer.textContent = formatDuration(sec);
  // CSS variable consumed by .topbar-record's box-shadow / accent so
  // the button's outline pulses with the master signal. Clamp to
  // [0,1] so a hot signal doesn't bleed.
  const peak = Math.min(1, recorder.getPeakLevel());
  recordBtn.style.setProperty('--record-peak', peak.toFixed(3));
  recordRafId = requestAnimationFrame(recordTick);
}
recorder.subscribe((state) => {
  if (state === 'recording' && recordRafId === null) {
    recordRafId = requestAnimationFrame(recordTick);
  }
});

// Auto-stop notification — when the safety ceiling fires, the user
// didn't press Stop, so they need a heads-up plus the download still
// goes through (we have a full result in hand).
recorder.onStop((reason, result) => {
  if (reason === 'maxDuration') {
    pwaToast.show({
      message: `Recording hit the 10-minute limit \u2014 saving anyway.`,
    });
  }
  Recorder.download(result);
});

recordBtn.addEventListener('click', () => {
  void ensureAudio().then(() => {
    if (recorder.getState() === 'idle') {
      recorder.start({
        ...(activeRecordingPresetName !== null
          ? { presetName: activeRecordingPresetName }
          : {}),
      });
    } else {
      // Manual stop — finalizeStop fires onStop which handles the
      // download. We don't need the result here.
      recorder.stop();
    }
  });
});

/** `MM:SS` for the duration readout. Drops hours since we cap at 10
 *  minutes; a recording can't naturally exceed that. */
function formatDuration(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

panicBtn.addEventListener('click', () => {
  // Kill both the arp (which may be mid-pattern) and every voice.
  // Without the arp panic, a held arp pattern would keep re-triggering
  // notes after the voice-panic silenced the pool.
  arp.panic();
  voices?.panic();

  // Stop the metronome too — it routes around master (so panicking
  // master gain wouldn't silence it) but the user pressing Panic
  // wants *everything* off, not just the synth voices.
  metronome.setEnabled(false);

  // Hard-mute master so the delay feedback loop, reverb tail, and
  // any other lingering wet signal all die. Hold silence for 250 ms
  // (long enough for typical tails to decay to inaudible against a
  // 0-gain master), then smoothly ramp back to the user's intended
  // level — read from the slider input, NOT `master.gain.value`,
  // because the live param is stale (already 0 from a prior panic in
  // flight, or mid-ramp from a slider drag). Reading the slider
  // means a double-tap Panic still works correctly: each press
  // re-mutes and re-restores to the intended level.
  try {
    const master = engine.master.gain;
    const t = engine.context.currentTime;
    const intended = parseFloat(masterVolumeInput.value);
    const restoreTo = Number.isFinite(intended) ? intended : DEFAULT_MASTER_VOLUME;
    master.cancelScheduledValues(t);
    master.setValueAtTime(0, t);
    master.setTargetAtTime(restoreTo, t + 0.25, 0.02);
  } catch {
    /* engine not live yet — arp/voices panic above is enough */
  }

  panicBtn.classList.add('armed');
  setTimeout(() => panicBtn.classList.remove('armed'), 180);
});

// ── MIDI engine is constructed here (not at the keyboard wiring site below)
// because VoiceControls needs to query it during its own construction.
// MIDI UI (picker, message routing) is still wired later alongside the
// on-screen keyboard.
const midi = new MidiEngine();

// ── Voice controls panel (rack-level): Mode + Glide.
//
// POLY is always available. QWERTY has limited n-key rollover so users won't
// get full chords from the keyboard, but letting them pick POLY (for sustain
// stacking, arpeggiation, or MIDI plug-in later) is worth it. The tooltip on
// the button explains the QWERTY caveat.
const voiceControls = new VoiceControls({
  getVoices: () => voices,
  initial: { mode: 'poly', glideMs: 0 },
});

// ── Macro bank — 8 performance controls that show up as mod sources in
// the matrix. Parameters exist from construction (so the UI can bind), but
// the ConstantSourceNodes wait for audio unlock before they're real.
const macroBank = new MacroBank(4);

// MIDI Learn registry — bind any knob to a controller CC. The
// resolver walks modules + the live effect chain + macros, returning
// the live NumericParameter for a given paramRef. Effects are
// resolved through `effectsChain.getModules()` so bindings survive
// add / remove / preset-load changes as long as the moduleId stays
// the same (deterministic ids on default effects, minted ids round-
// trip through preset save/load).
const midiBindings = new MidiBindings(midi, (ref) => {
  const m = /^module:([^:]+):(.+)$/.exec(ref);
  if (!m) return null;
  const moduleId = m[1]!;
  const paramName = m[2]!;
  // Collect everything addressable by id. Effect chain is dynamic;
  // the rest are static module references.
  const candidates: ReadonlyArray<{
    id: string;
    parameters: ReadonlyArray<{ name: string; kind: string }>;
  }> = [
    osc,
    filter,
    vca,
    env,
    lfo,
    sh,
    noise,
    arp,
    ...effectsChain.getModules(),
    ...macroBank.macros.map((mac) => ({
      id: mac.id,
      parameters: [mac.value],
    })),
  ];
  for (const mod of candidates) {
    if (mod.id !== moduleId) continue;
    const p = mod.parameters.find((x) => x.name === paramName);
    if (p && p.kind === 'numeric') {
      return p as unknown as import('@ac-slayer/synth-core').NumericParameter;
    }
    return null;
  }
  return null;
});

// ── Mod arc controller — lives from startup, gets wired to the router
// after first audio unlock. Every knob that's a mod target references it
// so arcs appear / update the moment the Mod Matrix changes.
const modArcs = new ModArcController();

// ── Supabase client + auth + preset manager. VoiceControls must exist before
// this so the preset manager can round-trip voice settings through its
// Parameters.
try {
  const supabase = getSupabaseClient();
  authManager = new AuthManager(supabase);
  const store = new SupabasePresetStore(supabase, authManager);
  // Cloud-backed pattern store — synchronous reads from a local
  // cache, writes mirrored to a Supabase `patterns` table on every
  // mutation. Hydrates from cloud on every signed-in transition so
  // a fresh device picks up the user's existing bank.
  patternStore = new SupabasePatternStore(supabase, authManager);
  // Non-effect modules — captured/restored as `modules` in the preset.
  // Effects round-trip separately through `effectsChain.serialize()` /
  // `applyPreset()` since they're dynamic in count and identity.
  const templateModules = [osc, filter, vca, env, lfo, sh, noise, arp, sequencer];
  presetManager = new PresetManager(
    {
      transport,
      modules: templateModules,
      getVoiceManager: () => voices,
      getRouter: () => router,
      voiceSettings: voiceControls,
      macroBank,
      effectsChain,
      midiBindings,
      // PresetManager fires this right before any applyState() — the
      // undo manager uses it to checkpoint the pre-load state so a
      // factory / user preset load is one Ctrl+Z away from the
      // patch the user was on.
      beforeApply: () => {
        if (history && !history.isApplying()) history.push();
      },
    },
    store,
  );
  // Build the history manager now that capture/apply are ready.
  // captureState is safe pre-unlock (voice/router fall back to
  // sensible defaults), so the manager has a baseline immediately.
  history = new HistoryManager({
    capture: () => presetManager!.captureState(),
    apply: (snap) => {
      // applyState wants a full Preset, but we're storing the lighter
      // PresetCapture. Synthesize a minimal Preset wrapper around the
      // capture — applyState only reads the capture-shaped fields.
      presetManager!.applyState({
        schemaVersion: 0 as never,
        id: '',
        name: '',
        category: '',
        tags: [],
        createdAt: 0,
        updatedAt: 0,
        ...snap,
      });
    },
    coalesceMs: 500,
    // 10-deep stack is plenty for "I changed my mind a few times"
    // workflows and keeps memory predictable — every snapshot is a
    // full preset capture (a few KB of plain JSON), so capping
    // shallow is cheap insurance against runaway growth.
    maxSize: 10,
  });
} catch (err) {
  supabaseReady = false;
  console.error('[supabase] setup failed', err);
}

// Undo / redo buttons — keep their disabled state in sync with the
// history manager. Subscribe fires immediately with the current
// state so the buttons render correctly from boot.
undoBtn.addEventListener('click', () => history?.undo());
redoBtn.addEventListener('click', () => history?.redo());
if (history) {
  history.subscribe(({ canUndo, canRedo }) => {
    undoBtn.disabled = !canUndo;
    redoBtn.disabled = !canRedo;
  });
} else {
  // Supabase setup failed → no history. Buttons stay permanently
  // disabled; HTML already declared them disabled, so this is a
  // belt-and-suspenders no-op.
  undoBtn.disabled = true;
  redoBtn.disabled = true;
}

// Preset browser — requires auth + presets wired above. If Supabase env
// vars are missing, show a visible error in the topbar instead of silently
// breaking the button.
const userMenuSlot = requireEl('user-menu-slot');

// ── Easter egg: secret fart preset bank ─────────────────────────────
// Held-Shift typing accumulates letters into a buffer; on a match the
// fart bank is revealed. The flag persists in localStorage so the
// presets stay unlocked across sessions. Read it now so the
// PresetBrowser is constructed with the right initial bank.
const FART_UNLOCK_KEY = 'evil-synth-fart-unlock';
let fartsUnlocked = false;
try {
  fartsUnlocked = localStorage.getItem(FART_UNLOCK_KEY) === '1';
} catch {
  // localStorage can throw in private-browsing or storage-disabled
  // contexts. Default to locked; the easter egg still works in-session,
  // it just won't persist.
  fartsUnlocked = false;
}
const initialFactoryBank: readonly Preset[] = fartsUnlocked
  ? [...FACTORY_PRESETS, ...FART_PRESETS]
  : FACTORY_PRESETS;

if (supabaseReady && authManager && presetManager) {
  const browser = new PresetBrowser({
    manager: presetManager,
    auth: authManager,
    factoryPresets: initialFactoryBank,
    onTest: () => playTestChord(),
  });
  presetsSlot.appendChild(browser.element);
  presetsToggle.addEventListener('click', () => browser.toggle());
  // Expose the browser at module scope so the randomizer (defined far
  // earlier in this file) can clear the active-preset label after a
  // roll. The `if (supabaseReady && ...)` guard above means this stays
  // null on configurations without preset persistence — the randomize
  // handler null-checks before calling clearActive().
  presetBrowser = browser;

  // Active-preset label — sits after the Presets/Test cluster in the
  // synth toolbar. Reads whichever patch the user most recently loaded
  // or saved; clears back to blank when the active preset is deleted
  // or the session signs out. Clicking the label opens the browser
  // (quality-of-life: the name is basically a second Presets button).
  const activePresetLabel = document.createElement('button');
  activePresetLabel.type = 'button';
  activePresetLabel.className = 'synth-toolbar-active-preset';
  activePresetLabel.hidden = true;
  activePresetLabel.addEventListener('click', () => browser.setOpen(true));
  presetsToggle.insertAdjacentElement('afterend', activePresetLabel);

  // Test button — inserted AFTER the label so the final DOM order is
  // [Presets, Test, label] (the second `afterend` insertion pushes the
  // first-inserted sibling further right). Lets users audition the
  // current patch without leaving the rack.
  const toolbarTestBtn = document.createElement('button');
  toolbarTestBtn.type = 'button';
  toolbarTestBtn.className = 'btn btn-sm';
  toolbarTestBtn.textContent = 'Test';
  toolbarTestBtn.title = 'Play a short triad with the current patch';
  toolbarTestBtn.addEventListener('click', () => playTestChord());
  presetsToggle.insertAdjacentElement('afterend', toolbarTestBtn);
  browser.subscribeActive((info) => {
    if (info) {
      activePresetLabel.textContent = info.name;
      activePresetLabel.title = `${info.name} · ${info.category}${info.factory ? ' (factory)' : ''}`;
      activePresetLabel.hidden = false;
      // Mirror the active patch name into the recorder so the WAV
      // filename includes whichever preset was loaded when the user
      // hit Record. Captured at start() time, not at stop(), so a
      // mid-recording preset switch doesn't rename the take.
      activeRecordingPresetName = info.name;
    } else {
      activePresetLabel.textContent = '';
      activePresetLabel.hidden = true;
      activeRecordingPresetName = null;
    }
  });

  // UserMenu — circular avatar at the right edge of the topbar. The
  // dropdown surfaces email + sign-out when signed in, or a sign-in
  // button when signed out. The sign-in button opens the preset
  // browser (which already hosts the inline auth form), so there's
  // only one place in the app where credentials get typed.
  const userMenu = new UserMenu({
    auth: authManager,
    onSignIn: () => browser.setOpen(true),
    install: installController,
  });
  userMenuSlot.appendChild(userMenu.element);

  // Fire off auth init so persisted sessions restore before first paint.
  void authManager.init();
} else {
  presetsToggle.disabled = true;
  presetsToggle.title = 'Supabase is not configured — set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.';
  // Nothing to authenticate against — leave the avatar slot empty.
}

// Two-way bind: typing in the BPM box sets transport.bpm, and external
// changes (preset loads, MIDI clock later) push back into the input.
bpmInput.value = String(transport.bpm.value);
bpmInput.addEventListener('input', () => {
  const v = parseFloat(bpmInput.value);
  if (Number.isFinite(v)) transport.bpm.set(v, 'ui');
});
transport.bpm.subscribe((v, source) => {
  if (source !== 'ui') bpmInput.value = String(Math.round(v));
});

// ── Tap tempo — click in time on the TAP button to set BPM. A pure
// TapTempo class does the math; this handler wires clicks to it and
// a brief flash class for visual feedback. The bpm input's own
// subscribe handler repaints its value when transport.bpm changes.
const tapTempoBtn = requireEl('tap-tempo-btn') as HTMLButtonElement;
const tapTempo = new TapTempo({ maxTaps: 4 });
tapTempoBtn.addEventListener('click', () => {
  const result = tapTempo.tap(performance.now());
  if (result.bpm !== null) {
    const rounded = Math.round(result.bpm);
    transport.bpm.set(rounded, 'ui');
    bpmInput.value = String(rounded);
  }
  // Brief pulse class so each tap gives visible feedback even before
  // two taps produce a BPM. Remove on animationend so the next tap
  // can re-trigger the animation.
  tapTempoBtn.classList.remove('tap-pulse');
  // Force reflow so removing and re-adding the class restarts the CSS animation.
  void tapTempoBtn.offsetWidth;
  tapTempoBtn.classList.add('tap-pulse');
});
tapTempoBtn.addEventListener('animationend', () => {
  tapTempoBtn.classList.remove('tap-pulse');
});

// ── Metronome — toggled on/off via a button inside the BPM cluster.
// Audio output bypasses engine.master and goes straight to destination,
// so the Recorder tap never sees it (the click track would bleed into
// the user's exported WAV otherwise). Idle pre-unlock: clicking the
// button still flips state, and the first gesture that unlocks audio
// will start the scheduler since Metronome.setEnabled caches intent.
const metronome = new Metronome();
const metronomeBtn = requireEl('metronome-btn') as HTMLButtonElement;
const metronomeVolumeInput = requireEl('metronome-volume') as HTMLInputElement;
metronomeBtn.addEventListener('click', () => {
  void ensureAudio().then(() => metronome.toggle());
});
metronome.subscribe((enabled) => {
  metronomeBtn.classList.toggle('on', enabled);
  metronomeBtn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
});
// Seed the slider from the current Metronome volume so HTML and
// model agree. Input events fire while the user is dragging; change
// fires on commit — both route to setVolume which smooths the gain.
metronomeVolumeInput.value = String(metronome.getVolume());
metronomeVolumeInput.addEventListener('input', () => {
  const v = parseFloat(metronomeVolumeInput.value);
  if (Number.isFinite(v)) metronome.setVolume(v);
});

// Touch-friendly access to the hover-only volume popovers. Tap on the
// master volume button toggles the slider open; tap on the metronome
// button keeps its primary "toggle metronome" behavior AND also opens
// the slider so a touch user has a path in. Outside-tap and Escape
// close either popover.
const masterVolumeWrap = masterVolumeInput.closest('.topbar-volume-wrap');
if (masterVolumeWrap instanceof HTMLElement) {
  const trigger = masterVolumeWrap.querySelector('.topbar-volume-button');
  attachTouchPopover({
    wrap: masterVolumeWrap,
    ...(trigger instanceof HTMLElement ? { trigger } : {}),
    toggle: true,
  });
}
const metronomeWrap = metronomeBtn.closest('.topbar-metronome-wrap');
if (metronomeWrap instanceof HTMLElement) {
  attachTouchPopover({
    wrap: metronomeWrap,
    trigger: metronomeBtn,
    // Metronome button has its own click action (toggle on/off), so
    // we additively open the volume popover — leave it open even if
    // the user toggles metronome a second time. They close it via
    // outside-tap.
    toggle: false,
  });
}

function setStatus(text: string, live = false): void {
  statusEl.textContent = text;
  statusEl.classList.toggle('live', live);
}
engine.onStateChange((s) => setStatus(`Engine: ${s}`, s === 'running'));
setStatus('Engine: closed');

// ── Rack (subtractive voice modules + modulation + global effects)
const rack = document.createElement('div');
rack.className = 'rack';

// ── SCOPE — live visualizer panel mounted as the first rack panel,
// inline alongside the rest. Reads as a leftmost lens that shows
// "what's coming out" of the whole patch. The Visualizer instance
// was constructed earlier (so it survives engine rebuilds via the
// shared attach() pattern); we just slot its element into a panel.
// The Capture controls live here too — the visualizer is exactly
// the wave you're about to grab, so source-pick + ⏺ Capture sits
// right under the scope where the user can already see what
// they'll get.
const scopePanel = new ModulePanel({ title: 'SCOPE' });
scopePanel.element.classList.add('scope-panel');
scopePanel.append(visualizer.element);

// ── Wavetable capture controls ────────────────────────────────────────
// "Capture" snapshots the audio at a chosen tap point and stores
// it as the OSC's custom wavetable. Wave is auto-switched to CAP
// so the user hears the result immediately. Source dropdown lets
// the user pick where to capture from — tapping FOLD's output is
// the workflow that uses the wavefolder as a wave-shaping element.
const captureWrap = document.createElement('div');
captureWrap.className = 'osc-capture';
const captureSourceSelect = document.createElement('select');
captureSourceSelect.className = 'osc-capture-source mono';
captureSourceSelect.title = 'Where to grab the wave shape from';
captureSourceSelect.setAttribute('aria-label', 'Capture source');
// Master is always available; the FOLD option is dynamically
// added/removed when a wavefolder is in / out of the chain.
{
  const opt = document.createElement('option');
  opt.value = 'master';
  opt.textContent = 'Master';
  captureSourceSelect.appendChild(opt);
}
const refreshCaptureSources = (): void => {
  // Drop any non-master options and re-add for current chain.
  for (let i = captureSourceSelect.options.length - 1; i > 0; i--) {
    captureSourceSelect.remove(i);
  }
  let foldIndex = 0;
  for (const m of effectsChain.getModules()) {
    if (m.type !== 'wavefolder') continue;
    foldIndex++;
    const opt = document.createElement('option');
    opt.value = `fx:${m.id}`;
    opt.textContent = foldIndex === 1 ? 'FOLD' : `FOLD ${foldIndex}`;
    captureSourceSelect.appendChild(opt);
  }
};
refreshCaptureSources();
effectsChain.subscribe(() => refreshCaptureSources());
captureWrap.appendChild(captureSourceSelect);

const captureBtn = document.createElement('button');
captureBtn.type = 'button';
captureBtn.className = 'btn btn-sm osc-capture-btn';
captureBtn.textContent = '⏺ Capture';
captureBtn.title =
  'Snapshot the current wave shape from the chosen source and use it as the OSC wave';
captureBtn.addEventListener('click', async () => {
  // Need a live engine — capture taps audio nodes that don't
  // exist before unlock. Calling ensureAudio() is cheap on a
  // running engine and unlocks one if needed.
  await ensureAudio();
  const sel = captureSourceSelect.value;
  let sourceNode: AudioNode | null = engine.master;
  if (sel.startsWith('fx:')) {
    const id = sel.slice(3);
    const mod = effectsChain.getModules().find((m) => m.id === id);
    sourceNode = mod?.output ?? null;
  }
  if (!sourceNode) return;
  captureBtn.disabled = true;
  try {
    const wave = await captureWavetable(engine.context, sourceNode);
    osc.setCapturedWavetable(wave);
    // Auto-flip Wave to CAP so the user hears the new shape
    // immediately. If they already set it manually, this is
    // idempotent.
    osc.waveform.set('capture', 'ui');
  } finally {
    captureBtn.disabled = false;
  }
});
captureWrap.appendChild(captureBtn);
scopePanel.append(captureWrap);

rack.appendChild(scopePanel.element);

// ── SOURCE — merged OSC + Voice + Noise.
// Three sub-sections in a single panel. Each sub-section is its own
// flex row, stacked vertically — so the outer panel's width stays equal
// to the widest sub-section instead of ballooning to fit them all.
const sourcePanel = new ModulePanel({ title: 'SOURCE' });
// OSC + FOLD share a horizontal row so the eye reads them as one
// "tone-shaping" stage with FOLD sitting visually downstream of OSC.
// The left-border accent on the FOLD subgroup becomes the vertical
// divider between the two. VOICE / NOISE keep stacking below as
// before — `oscFoldRow` is a single child of the column-flow content.
const oscFoldRow = document.createElement('div');
oscFoldRow.className = 'source-osc-fold-row';
sourcePanel.content.appendChild(oscFoldRow);
const oscBody = makeSubsection(oscFoldRow, 'OSC');
// Drive + Fold get their own FOLD subsection beside OSC. They're a
// per-voice wave-shaping stage on top of the source, not just
// another knob next to Coarse / Detune.
const FOLD_PARAM_NAMES = new Set(['Drive', 'Fold']);
for (const p of osc.parameters) {
  if (FOLD_PARAM_NAMES.has(p.name)) continue;
  sourcePanel.addParamControl(p, { moduleId: osc.id, modArcs, midiBindings, host: oscBody });
}

// FOLD subsection — per-voice wavefolder. Same `sin(πx·fold)` curve
// as the FOLD effect, but applied inside each Voice between the
// oscillator stage and the filter. Drive crossfades dry/wet AND
// pre-amplifies the input so cranking it both adds harmonics and
// drives them harder. Fold sets the curve's harmonic density.
// Sits to the right of OSC inside `oscFoldRow`; the left-border
// accent on `.module-panel-subgroup-fold` is the divider between
// the two subsections.
const foldBody = makeSubsection(oscFoldRow, 'FOLD', 'fold');
for (const p of osc.parameters) {
  if (!FOLD_PARAM_NAMES.has(p.name)) continue;
  sourcePanel.addParamControl(p, { moduleId: osc.id, modArcs, midiBindings, host: foldBody });
}

// VOICE joins OSC + FOLD inside the same horizontal row so the eye
// reads them as a single per-note tone-shaping stack. The 'voice'
// modifier picks up the same left-border accent as FOLD / NOISE,
// which gives a clean vertical divider between FOLD and VOICE.
const voiceBody = makeSubsection(oscFoldRow, 'VOICE', 'voice');
// modeControl includes the "Mode" label above the POLY/MONO/LEG buttons
// so users see what the segmented group is for.
voiceBody.appendChild(voiceControls.modeControl);
voiceBody.appendChild(voiceControls.glideKnob.element);
voiceBody.appendChild(voiceControls.unisonKnob.element);
voiceBody.appendChild(voiceControls.spreadKnob.element);
const noiseBody = makeSubsection(sourcePanel.content, 'NOISE', 'noise');
for (const p of noise.parameters) {
  sourcePanel.addParamControl(p, { moduleId: noise.id, modArcs, midiBindings, host: noiseBody });
}
rack.appendChild(sourcePanel.element);

rack.appendChild(ModulePanel.fromModule(filter, { modArcs, midiBindings }).element);
rack.appendChild(ModulePanel.fromModule(env, { modArcs, midiBindings }).element);
rack.appendChild(ModulePanel.fromModule(vca, { modArcs, midiBindings }).element);

// ── MOD — LFO + S&H + MACROS in one panel. Every modulator lives
// together; macros included since they're themselves a mod source
// in the matrix. MACROS picks up a left-border accent (same visual
// language as NOISE in SOURCE) to signal the category shift.
const modPanel = new ModulePanel({ title: 'MOD' });
const lfoBody = makeSubsection(modPanel.content, 'LFO');
for (const p of lfo.parameters) {
  modPanel.addParamControl(p, { moduleId: lfo.id, modArcs, midiBindings, host: lfoBody });
}
const shBody = makeSubsection(modPanel.content, 'S&H', 'sh');
for (const p of sh.parameters) {
  modPanel.addParamControl(p, { moduleId: sh.id, modArcs, midiBindings, host: shBody });
}

// WAVE — taps an audio node and exposes its signal as a mod source.
// The actual AudioNode wiring lives in WaveModSource; this subsection
// is just the "where to tap" picker. Same Master / FOLD options as the
// Capture controls, but they're independent — capture freezes a wave
// shape into the OSC, WAVE feeds the matrix at audio rate.
const waveModBody = makeSubsection(modPanel.content, 'WAVE', 'wave');
const waveSourceSelect = document.createElement('select');
waveSourceSelect.className = 'osc-capture-source mono';
waveSourceSelect.title = 'Audio node to use as the WAVE mod source';
waveSourceSelect.setAttribute('aria-label', 'WAVE source');
{
  const opt = document.createElement('option');
  opt.value = 'master';
  opt.textContent = 'Master';
  waveSourceSelect.appendChild(opt);
}
const refreshWaveSources = (): void => {
  const prev = waveSourceSelect.value;
  for (let i = waveSourceSelect.options.length - 1; i > 0; i--) {
    waveSourceSelect.remove(i);
  }
  let foldIndex = 0;
  for (const m of effectsChain.getModules()) {
    if (m.type !== 'wavefolder') continue;
    foldIndex++;
    const opt = document.createElement('option');
    opt.value = `fx:${m.id}`;
    opt.textContent = foldIndex === 1 ? 'FOLD' : `FOLD ${foldIndex}`;
    waveSourceSelect.appendChild(opt);
  }
  // Restore prior selection if it still exists; otherwise drop back
  // to Master so the source isn't silently broken after a chain edit.
  const stillExists = Array.from(waveSourceSelect.options).some(
    (o) => o.value === prev,
  );
  waveSourceSelect.value = stillExists ? prev : 'master';
};
refreshWaveSources();
effectsChain.subscribe(() => {
  refreshWaveSources();
  applyWaveModSource();
});
waveModBody.appendChild(waveSourceSelect);

// Resolve the AudioNode that the dropdown's current value points at.
// Reused both at registration time and on dropdown change.
const resolveWaveSource = (): AudioNode | null => {
  if (!waveModSource) return null;
  const sel = waveSourceSelect.value;
  if (sel.startsWith('fx:')) {
    const id = sel.slice(3);
    const mod = effectsChain.getModules().find((m) => m.id === id);
    return mod?.output ?? null;
  }
  return engine.master;
};
const applyWaveModSource = (): void => {
  if (!waveModSource) return;
  waveModSource.setSource(resolveWaveSource());
};
waveSourceSelect.addEventListener('change', applyWaveModSource);

const macroBody = makeSubsection(modPanel.content, 'MACROS', 'macros');
for (const macro of macroBank.macros) {
  macroBody.appendChild(
    new Knob({
      param: macro.value,
      label: macro.label,
      moduleId: macro.id,
      modArcs,
      midiBindings,
      midiParamRef: `module:${macro.id}:Value`,
    }).element,
  );
}

// ── Move the Mod Matrix toggle into the MOD panel's header, right-
// aligned. The topbar button in index.html stays as a hidden hook —
// it's the event target the keyboard shortcut (Ctrl+M) and the moved
// button both click, so the wiring below doesn't have to fork.
{
  const modHeader = modPanel.element.querySelector('.module-panel-header');
  if (modHeader) {
    const modMatrixBtn = document.createElement('button');
    modMatrixBtn.type = 'button';
    modMatrixBtn.className = 'btn btn-sm module-panel-action';
    modMatrixBtn.textContent = 'Mod Matrix';
    modMatrixBtn.title = 'Open the modulation matrix';
    modMatrixBtn.addEventListener('click', () => toggleMatrix());
    modHeader.appendChild(modMatrixBtn);
  }
}

rack.appendChild(modPanel.element);

// Effects rack — reorderable via drag-drop on the sub-panel headers,
// and the +/✕ palette adds or removes effect instances. Chain changes
// rebuild the audio graph and refresh the mod matrix's targets.
const effectsGroup = new EffectsGroup({
  chain: effectsChain,
  registry: effectRegistry,
  modArcs,
  midiBindings,
});
rack.appendChild(effectsGroup.element);

/**
 * Refresh router targets, the arc controller's view, and the mod matrix
 * panel after the effects chain has changed (add / remove / reorder).
 * Each piece subscribes to the chain elsewhere; centralizing the work
 * here keeps the four call sites in lockstep.
 */
function syncTargetsForChainChange(): void {
  const next = currentTargets();
  router?.setTargets(next);
  modArcs.setTargets(next);
  matrix?.setTargets(next);
}
// Subscribe ASAP so subsequent chain mutations (preset load, randomize,
// drag-drop, palette add/remove) propagate to the router and matrix.
effectsChain.subscribe(() => syncTargetsForChainChange());

const phase = document.createElement('div');
phase.className = 'phase';
phase.appendChild(rack);
stage.appendChild(phase);

/**
 * Audition the current patch by playing a short arpeggiated triad
 * (C major, M→P5→octave, 150ms between hits). Bypasses the arp so
 * the user hears the dialed-in voice at full breadth rather than
 * one note at a time. Resumes audio first — safe to call from any
 * click handler without a separate ensureAudio step.
 */
function playTestChord(): void {
  void ensureAudio().then(() => {
    if (!voices) return;
    const t0 = engine.context.currentTime;
    voices.noteOn(60, 0.9, t0);
    voices.noteOn(67, 0.9, t0 + 0.15);
    voices.noteOn(72, 0.9, t0 + 0.3);
    voices.noteOff(60, t0 + 0.55);
    voices.noteOff(67, t0 + 0.55);
    voices.noteOff(72, t0 + 0.55);
  });
}

// ── Keyboard — Test moved out of the dock into the synth toolbar
// (and duplicated inside the preset browser) so patch-auditioning
// lives with the other patch controls.
const keyboard = new KeyboardDock({
  onNoteOn: (note, velocity) => {
    // When the Atom panel's trigger is set to chord/arp, the keyboard
    // intercepts: pressing a key plays (or arpeggiates) the current
    // element's chord transposed to that key, instead of single notes.
    // The panel's playAt is idempotent for repeated presses on the
    // same key so this is safe to call eagerly.
    void ensureAudio().then(() => {
      if (atomPanel?.isTriggerActive()) {
        atomPanel.playAt(note, velocity);
      } else {
        arp.noteOn(note, velocity);
      }
    });
  },
  onNoteOff: (note) => {
    // Always also drop through to arp.noteOff — covers the case where
    // the user changed the trigger mode mid-hold and the original
    // noteOn went through arp before the switch. arp.noteOff for
    // unknown notes is a no-op so the duplicate call is harmless.
    if (atomPanel?.isTriggerActive()) atomPanel.releaseAt(note);
    arp.noteOff(note);
  },
  onPitchBend: (amount) => {
    void ensureAudio().then(() => setPitchBend(amount));
  },
});
keyboardSlot.appendChild(keyboard.element);

// Reparent the BPM cluster and MIDI picker slot into the dock's
// controls-right cluster — the user wants tempo/MIDI to live in the
// same toolbar as the Kbd / Arp / Seq buttons rather than in the
// patch toolbar above the rack. The HTML declares them inside the
// hidden #reparent-store container so requireEl() can find them on
// boot before they get moved here.
const bpmCluster = bpmInput.closest('.topbar-bpm');
if (bpmCluster instanceof HTMLElement) {
  keyboard.appendToControlsRight(bpmCluster, midiSlot);
}

// Reparent the topbar's center cluster (Panic / Volume / Meter /
// Record) into the dock's controls-center slot. The user wants the
// global transport controls living alongside the BPM/MIDI cluster
// rather than in the very top nav. Each child is moved individually
// so the topbar-center wrapper doesn't come along — the dock uses
// its own grid spacing and doesn't need the topbar's flex container.
const topbarCenter = document.querySelector('.topbar-center');
if (topbarCenter instanceof HTMLElement) {
  const movables: HTMLElement[] = [];
  for (const child of Array.from(topbarCenter.children)) {
    if (child instanceof HTMLElement) movables.push(child);
  }
  keyboard.appendToControlsCenter(...movables);
  // Empty container can stay in the topbar grid — it occupies the
  // center column with zero width, keeping the title left and the
  // user menu right via the existing `1fr auto 1fr` layout.
}

// Arp tab — uses the same keyboard-tab structure as the keyboard and
// sequencer tabs. Mode + Rate live in the header (the most-used
// settings); Gate + Octaves sit in the body. The module's `enabled`
// param is no longer surfaced as a UI toggle — selecting the Arp tab
// turns the arp on, switching to another tab turns it off (wired
// below via subscribeTabChange).
const arpTabEl = document.createElement('div');
arpTabEl.className = 'keyboard-tab keyboard-arp-panel';
const arpTabHeader = document.createElement('div');
arpTabHeader.className = 'keyboard-tab-header';
arpTabEl.appendChild(arpTabHeader);
const arpTabBody = document.createElement('div');
arpTabBody.className = 'keyboard-tab-body';
arpTabEl.appendChild(arpTabBody);

// addParamControl is the right tool for placing per-parameter Knob /
// WaveSelect / SelectControl elements with the standard styling and
// mod-arc / MIDI-learn integration. We instantiate a temporary
// ModulePanel solely to call its addParamControl method with custom
// host elements; the panel's own .element is never mounted.
const arpPanelBuilder = new ModulePanel({ title: 'ARP' });
arpPanelBuilder.addParamControl(arp.mode, {
  moduleId: arp.id,
  modArcs,
  midiBindings,
  host: arpTabHeader,
});
arpPanelBuilder.addParamControl(arp.rate, {
  moduleId: arp.id,
  modArcs,
  midiBindings,
  host: arpTabHeader,
});
arpPanelBuilder.addParamControl(arp.gateLength, {
  moduleId: arp.id,
  modArcs,
  midiBindings,
  host: arpTabBody,
});
arpPanelBuilder.addParamControl(arp.octaves, {
  moduleId: arp.id,
  modArcs,
  midiBindings,
  host: arpTabBody,
});
keyboard.setArpElement(arpTabEl);

// Tab-driven arp enable: turn the arp on while its tab is the active
// view, off otherwise. Replaces the old explicit Enabled toggle in
// the arp panel — the user wanted "selecting the Arp tab is the
// switch". subscribeTabChange fires immediately with the current
// tab so the initial state syncs without an extra priming call.
keyboard.subscribeTabChange((tab) => {
  arp.enabled.set(tab === 'arp' ? 'on' : 'off', 'ui');
});

// Keep the badge dot lit whenever arp.enabled is 'on' — covers
// preset loads or any other path that flips the param outside our
// tab-change handler. The tab-change handler keeps the param in
// sync; this keeps the visual indicator in sync.
keyboard.setArpEnabledIndicator(arp.enabled.value === 'on');
arp.enabled.subscribe((v) => keyboard.setArpEnabledIndicator(v === 'on'));

// Sequencer panel — same tab pattern as the arp. Its custom UI handles
// step editing + transport; the module's serialize() / deserialize()
// covers preset round-trip. The pattern store lives in localStorage
// so users can save / recall sequencer patterns independently of the
// full preset they're tied to.
// Cloud-backed pattern store gets constructed inside the supabase
// try block above when env vars are present. If supabase setup
// failed (missing env, malformed URL, etc.), fall back to a plain
// local-only PatternStore so the seq tab still works.
patternStore ??= new PatternStore();
const sequencerPanel = new SequencerPanel({
  sequencer,
  patternStore,
  // The seq's noteOn handler routes directly to the VoiceManager
  // (no arp passthrough), so it's the only path that doesn't go
  // through ensureAudio. Wire it explicitly here so hitting Play
  // unlocks the AudioContext on first interaction, instead of the
  // ticks firing silently until the user plays a note elsewhere.
  onPlay: () => ensureAudio(),
});
keyboard.setSeqElement(sequencerPanel.element);
keyboard.setSeqEnabledIndicator(sequencer.enabled.value === 'on');
sequencer.enabled.subscribe((v) => keyboard.setSeqEnabledIndicator(v === 'on'));

// ── Song sequencer — chains saved patterns from the PatternStore
// into a song-level arrangement. Uses the same `sequencer` instance
// as the Seq tab; on each pattern wrap it splats in the next block's
// pattern via setSteps + length.set.
const songSequencer = new SongSequencer(sequencer, patternStore);
const songPanel = new SongPanel({ song: songSequencer, patternStore });
keyboard.setSongElement(songPanel.element);
keyboard.setSongEnabledIndicator(songSequencer.playing());
songSequencer.subscribe((s) => keyboard.setSongEnabledIndicator(s.playing()));
// If the user manually flicks the Seq tab's Play button while the
// song is running, the song's listener catches the seq turning off
// and stops itself. Without this the song would silently keep
// thinking it's playing through a stopped seq.
sequencer.enabled.subscribe((v) => {
  if (v === 'off' && songSequencer.playing()) songSequencer.stop();
});

// ── Atom panel — element-driven mod source UI. The AtomSource itself
// was constructed at module top so its preset-roundtrip parameters
// exist before the preset manager scans modules; the panel just owns
// the visualization + controls. Routes notes directly through the
// VoiceManager (skipping the arp) so trigger='chord' / 'arp' produce
// clean polyphony without double-arping.
atomPanel = new AtomPanel({
  source: atomSource,
  onNoteOn: (midi, velocity) => {
    void ensureAudio();
    voices?.noteOn(midi, velocity);
  },
  onNoteOff: (midi) => {
    voices?.noteOff(midi);
  },
});
keyboard.setAtomElement(atomPanel.element);

// ── MIDI — topbar picker + message routing. The `midi` engine itself was
// constructed earlier (VoiceControls needed it at construction time).
// Permission request is deferred until the user clicks "Enable" (Web MIDI
// permission prompts require a gesture on most browsers), or we best-effort
// probe on any audio-unlock gesture.
const midiPicker = new MidiPicker({
  engine: midi,
  onRequestPermission: async () => {
    await midi.init();
  },
});
midiSlot.appendChild(midiPicker.element);

// Tiny popover next to the picker showing recent incoming MIDI traffic.
// Helps users sanity-check connections, identify CC numbers, and debug
// "is the wheel doing anything?" without leaving the synth.
const midiMonitor = new MidiMonitor({ engine: midi });
midiSlot.appendChild(midiMonitor.element);

midi.onMessage(async (msg) => {
  // Every MIDI note path goes through ensureAudio first so the first
  // incoming note unlocks the engine the same way a keyboard click does.
  // Notes feed the arp — it either holds them (arp on) or passes them
  // straight through to the voice pool (arp off).
  switch (msg.type) {
    case 'noteOn':
      await ensureAudio();
      arp.noteOn(msg.note, msg.velocity);
      break;
    case 'noteOff':
      arp.noteOff(msg.note);
      break;
    case 'allNotesOff':
      arp.panic();
      voices?.panic();
      break;
    case 'pitchBend':
      await ensureAudio();
      setPitchBend(msg.value);
      break;
    case 'cc':
      // Mod wheel drives the global modwheel source. Other CCs are
      // either consumed by MidiBindings (Learn-mode mappings) directly
      // off the engine, or ignored here.
      if (msg.controller === CC.MOD_WHEEL) {
        await ensureAudio();
        setModWheel(msg.value / 127);
      }
      break;
    default:
      break;
  }
});

// Probe permission quietly on first unlock — if the browser has already
// granted MIDI to this origin, the prompt won't appear a second time.
void (async () => {
  // Delay just enough that the UI paints first.
  await new Promise((r) => setTimeout(r, 0));
  if (midi.currentState.kind === 'pending') await midi.init();
})();

// ── Mod matrix toggle — panel is created eagerly at boot now, so this
// handler only resumes audio and flips the panel's hidden state.
// Shared by the MOD panel's header button and the Ctrl+M shortcut.
function toggleMatrix(): void {
  void ensureAudio().then(() => matrix?.toggle());
}

/**
 * Build the entire audio graph against the live (suspended-or-running)
 * engine. Idempotent on `voices`: subsequent calls are a no-op.
 *
 * Runs at page load against a suspended context so the rack renders
 * with default effects, voices, and mod matrix populated immediately.
 * No audio flows until the first `engine.unlock()` call lands inside
 * a user gesture — but every panel and dropdown is fully wired before
 * that.
 */
async function buildAudioGraph(): Promise<void> {
  if (voices) return;

  // Master meter can attach now that engine.master is live. Safe to call
  // before modules init — the meter just reads amplitude off master.
  outputMeter.attach(engine);
  visualizer.attach(engine);

  // Recorder taps the same master signal. Attaching is cheap (just
  // stores a reference); the actual ScriptProcessor is constructed
  // on `start()` and torn down on `stop()` so idle Recorder costs
  // nothing in the audio graph.
  recorder.attach(engine);

  // Metronome — attaches to engine + transport but only builds audio
  // nodes when enabled. Its output routes straight to ctx.destination
  // (not engine.master), so the recorder never captures clicks.
  metronome.attach(engine, transport);

  // Load worklets BEFORE any module that needs them calls init() — the
  // AudioWorkletNode constructor throws if its processor hasn't been
  // registered. loadWorklet is idempotent so this is cheap to re-enter.
  // Worklet module loading works on a suspended context, so this can
  // run at page load with no user gesture.
  try {
    await engine.loadWorklet(getBitcrusherWorkletUrl());
  } catch (err) {
    console.warn('[fx] bitcrusher worklet failed to load', err);
  }
  try {
    await engine.loadWorklet(getGranularWorkletUrl());
  } catch (err) {
    console.warn('[fx] granular worklet failed to load', err);
  }

  // Init voice-side modulators (LFO/S&H) and the per-voice noise.
  lfo.init(engine);
  sh.init(engine);
  noise.init(engine);

  // Bootstrap the post-voice effect chain. setEngine first so add /
  // applyPreset rewire correctly; then either load the saved default
  // composition or seed a fresh one. Effects mint with deterministic
  // ids matching the prior singletons so v3-and-older mod connections
  // (via migration) still find their targets. Apply per-effect default
  // params right after construction so the patch sounds intentional.
  effectsChain.setEngine(engine);
  if (effectsChain.getModules().length === 0) {
    // Effects that boot bypassed on a fresh patch. Chorus / flanger /
    // phaser all push pitch around in similar ways and stacking them
    // muddies the sound — keep them in the chain so users can flip
    // them on individually without adding from the palette, but
    // start them off.
    const DEFAULT_BYPASSED = new Set(['chorus', 'flanger', 'phaser']);
    for (const { type, id } of DEFAULT_EFFECT_CHAIN) {
      const inst = effectRegistry.create(type, id);
      if (!inst) continue;
      inst.init(engine);
      if (DEFAULT_BYPASSED.has(type)) inst.bypass = true;
      effectsChain.adoptEffect(inst);
      const defaults = EFFECT_DEFAULTS[type];
      if (defaults) {
        const desStateParams: Record<string, number | string> = {};
        for (const [k, v] of Object.entries(defaults)) desStateParams[k] = v;
        inst.deserialize({
          id,
          type,
          params: desStateParams,
        });
      }
    }
    // First-time-only init patch — overwrites the default param values
    // we just dialed in with the curated starting patch the user
    // shipped. Narrow apply (just module + effect deserialize) so we
    // don't trigger the heavier rebuild paths in PresetManager.applyState
    // that broke things last time. Voice settings, mod connections,
    // BPM, and macros already match runtime defaults so they're skipped.
    applyInitPresetParams(
      [osc, filter, vca, env, lfo, sh, noise, arp, sequencer],
      effectsChain,
    );
  }
  // Noise is per-voice: each Voice creates its own BufferSource pointed
  // at NoiseModule's shared buffer and routes it into the voice's filter
  // alongside the osc. So it rides the envelope + VCA — turning Level
  // down or playing no notes silences it. No global noise → voiceBus
  // connection needed.

  // Shared pitch-bend signal. Feeds every voice's osc.detune.
  pitchBendSource = engine.context.createConstantSource();
  pitchBendSource.offset.value = 0;
  pitchBendSource.start();

  // Shared mod-wheel signal. CC 1 writes to its offset; router fans it
  // out to whatever the user maps it to in the Mod Matrix. Started here
  // so it's already running by the time the first CC arrives.
  modWheelSource = engine.context.createConstantSource();
  modWheelSource.offset.value = 0;
  modWheelSource.start();

  const voiceTemplate: VoiceTemplate = { ...template, noise, pitchBendSource };
  voices = new VoiceManager(engine, voiceTemplate, {
    polyphony: 8,
    mode: voiceControls.getMode(),
    glideMs: voiceControls.getGlideMs(),
    unison: Math.round(voiceControls.unison.value),
    unisonSpread: voiceControls.spread.value,
  });

  const targetList = currentTargets();
  router = new ModRouter(engine, voices, targetList, { macroBank });
  // Hand the router off to the arc controller so every knob can start
  // drawing its mod overlays. Must happen after construction so source
  // registrations below emit a change event that reaches the UI.
  modArcs.setRouter(router, targetList);
  if (lfo.modOutput) {
    router.registerSource(lfo.id, lfo.modOutput, 'LFO', 'Modulators');
    modArcs.setSourceKind(lfo.id, 'lfo');
  }
  if (sh.modOutput) {
    router.registerSource(sh.id, sh.modOutput, 'S&H', 'Modulators');
    modArcs.setSourceKind(sh.id, 'sh');
  }

  // Macros — init their audio-side ConstantSourceNodes, then register each
  // as a preset-scope mod source so they show up in the Mod Matrix.
  macroBank.init(engine);
  for (const m of macroBank.macros) {
    if (m.modOutput) router.registerSource(m.id, m.modOutput, m.label, 'Macros');
  }

  // Mod wheel — register as a preset-scope source so users can route
  // their controller's CC 1 to anything (filter cutoff, LFO depth, etc.).
  // The actual CC traffic is handled in the midi.onMessage switch below;
  // this just gets the signal node on the router so the matrix sees it.
  if (modWheelSource) {
    router.registerSource('modwheel', modWheelSource, 'Mod Wheel', 'Modulators');
  }

  // Sequencer — init its modOutput ConstantSourceNode and register
  // with the router so the per-step mod lane shows up as STEP in the
  // matrix's source dropdown. Note output is wired separately above
  // via setHandlers — that lives at the note-routing layer (same
  // shape as the arp), independent of the audio-rate mod source.
  sequencer.init(engine);
  if (sequencer.modOutput) {
    router.registerSource(sequencer.id, sequencer.modOutput, 'STEP', 'Modulators');
  }

  // ATOM — element-driven mod source. Registered alongside the
  // built-in modulators so it shows up in the matrix's source
  // dropdown next to LFO / S&H / STEP. Init has to happen here
  // (after engine unlock) so the ConstantSourceNode is built on
  // the live AudioContext; the panel can be constructed earlier.
  atomSource.init(engine);
  if (atomSource.modOutput) {
    router.registerSource(atomSource.id, atomSource.modOutput, 'ATOM', 'Modulators');
  }

  // WAVE — register the wave-shape mod source so users can route any
  // audio tap (master, fold output, ...) into the matrix. Built fresh
  // per engine init so it always points at the live AudioContext;
  // the source-pick dropdown applies its current selection right
  // after registration.
  if (waveModSource) waveModSource.dispose();
  waveModSource = new WaveModSource(engine, router);
  waveModSource.register();
  applyWaveModSource();
  // Default modulations — LFO wobbles cutoff, velocity opens filter.
  router.addConnection({
    sourceModuleId: lfo.id,
    targetModuleId: filter.id,
    targetParam: 'Cutoff',
    depth: 0.4,
  });
  router.addConnection({
    sourceModuleId: 'velocity',
    targetModuleId: filter.id,
    targetParam: 'Cutoff',
    depth: 0.3,
  });

  matrix = new ModMatrix({
    router,
    template,
    // Pass the same globals shape the router uses. Effects come from
    // the chain so adds / removes propagate automatically; non-effect
    // globals (LFO, S&H, NOISE) are static.
    globals: {
      lfo,
      sh,
      noise,
      effects: effectsChain.getModules() as readonly GlobalTargetProvider[],
    },
    macroBank,
  });
  matrixSlot.appendChild(matrix.element);
  // Now that the matrix exists, kick off a refresh to surface any
  // chain-driven additions that happened pre-router.
  syncTargetsForChainChange();

  // Wire undo/redo subscriptions now that every parameter exists in
  // its final form. The graph build is idempotent (no-op on re-entry),
  // so this attachment is one-time and matches the manager's
  // captureFn against the live state.
  attachHistoryHooks();
  // Re-seed the baseline now that the audio graph is live. Any
  // capture taken before this point used pre-unlock fallbacks
  // (voices=null, router=null) — those snapshots are stale relative
  // to the running synth, but a fresh reseed clears that.
  history?.reseed();
}

/**
 * Hook every Parameter the user can drive to the undo manager via
 * coalesced scheduling. A `source === 'ui'` change starts (or
 * extends) a debounced burst; when it settles, the burst lands as
 * one undo step. Subscriptions are additive, so existing UI
 * subscribers (Knob, ModArcController) keep working unchanged.
 *
 * Effects are dynamic — when the chain rebuilds (preset load, palette
 * add/remove, randomize), we re-walk it and subscribe newly-minted
 * parameters. The `subscribed` set keeps us from double-binding.
 */
const subscribedParams = new WeakSet<object>();
function attachHistoryHooks(): void {
  if (!history) return;
  const h = history;

  const hookParam = (
    p: import('@ac-slayer/synth-core').AnyParameter,
  ): void => {
    if (subscribedParams.has(p)) return;
    subscribedParams.add(p);
    // Both NumericParameter and EnumParameter expose subscribe with
    // (value, source) signatures; we only care about the source tag.
    // Cast to a permissive shape since the two listener types differ
    // only in the value's static type.
    const subscribe = (
      p as unknown as {
        subscribe: (
          fn: (v: unknown, source: import('@ac-slayer/synth-core').ParamSource) => void,
        ) => () => void;
      }
    ).subscribe;
    subscribe.call(p, (_v, source) => {
      // Only user-driven changes count toward the undo timeline.
      // Modulation, MIDI, preset load, randomize, etc. either
      // checkpoint elsewhere (preset/randomize) or are derived
      // from already-tracked state (modulation, macros).
      if (source !== 'ui') return;
      h.scheduleCoalesced();
    });
  };

  const walkAllParams = (): void => {
    const modules = [
      osc, filter, vca, env, lfo, sh, noise, arp,
      ...effectsChain.getModules(),
    ];
    for (const mod of modules) {
      for (const p of mod.parameters) hookParam(p);
    }
    // Voice-level Parameters (mode/glide/unison/spread).
    hookParam(voiceControls.glide);
    hookParam(voiceControls.unison);
    hookParam(voiceControls.spread);
    // Macro values.
    for (const m of macroBank.macros) hookParam(m.value);
    // Transport BPM.
    hookParam(transport.bpm);
  };

  walkAllParams();
  // Re-walk after the chain changes — newly minted effects need
  // their parameters subscribed.
  effectsChain.subscribe(() => walkAllParams());

  // Mod matrix changes — add/remove/edit on connections counts as
  // a user action. Coalesce since drag-edits on a depth slider can
  // fire many small changes in a row.
  router?.subscribe(() => {
    if (!h.isApplying()) h.scheduleCoalesced();
  });
}

/**
 * Resume the AudioContext so audio actually flows. Must be called
 * from inside a user gesture — click, keydown, MIDI note. Cheap on
 * subsequent calls (the engine no-ops on already-running contexts).
 *
 * The audio graph itself is built up at page load by `buildAudioGraph`
 * against a suspended context, so this only handles the resume step.
 */
async function ensureAudio(): Promise<void> {
  await engine.unlock();
}

// ── Page-load bootstrap. Build the AudioContext (suspended), wire up
// every module, populate the default effect chain — all without
// requiring a user gesture. Audio doesn't actually flow until the
// first `ensureAudio()` lands inside a click/keydown, but the rack
// is fully populated the moment the user lands on the page.
engine.prepare();
// Wire tab-visibility / focus → context.resume() so the synth wakes
// up on its own when the user returns from a backgrounded tab.
// Some browsers (notably iOS Safari) suspend AudioContexts in the
// background and don't auto-resume on visibility-change. Also wires
// `mediaDevices.devicechange` so we can warn the user when their
// audio output swaps mid-session.
engine.attachLifecycleHandlers();

// Audio device change — surface a toast so the user knows the synth
// might not be hearing the new device's preferred sample rate. Web
// Audio doesn't let us migrate an active AudioContext to a fresh
// rate, so the only fix is a page refresh — which the toast offers
// as an explicit action. Debounced to once-per-30s so a flaky
// Bluetooth connection doesn't spam.
let lastDeviceChangeToast = 0;
engine.onDeviceChange(() => {
  const now = Date.now();
  if (now - lastDeviceChangeToast < 30_000) return;
  lastDeviceChangeToast = now;
  pwaToast.show({
    message: 'Audio device changed. Refresh to use the new device.',
    action: {
      label: 'Refresh',
      onClick: () => window.location.reload(),
    },
  });
});

void buildAudioGraph().catch((err) => {
  console.error('[boot] audio graph build failed', err);
});

// ── DevTools bag. Exposed in all builds (not just dev) so production issues
// can be diagnosed from the console. None of this is sensitive — it's just
// references to live objects already present in the DOM / localStorage.
interface Dev {
  engine: AudioEngine;
  template: VoiceTemplate;
  getVoices(): VoiceManager | null;
  getRouter(): ModRouter | null;
  keyboard: KeyboardDock;
  presetManager: PresetManager | null;
  authManager: AuthManager | null;
}
(window as unknown as { __synth: Dev }).__synth = {
  engine,
  template,
  getVoices: () => voices,
  getRouter: () => router,
  keyboard,
  presetManager,
  authManager,
};
